import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ArrowUp,
  ArrowDown,
  Pencil,
  Trash2,
  GripVertical,
} from 'lucide-react'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import { trackApplicationEvent } from '@/lib/utils/analytics'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import {
  ListingDataTableCard,
  ListingDataTableScroll,
  LISTING_DATA_TABLE_CLASS,
} from '@/components/ui/listingDataTable'
import { PageShell } from '@/components/layout/PageShell'
import { ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { useIsMobile } from '@/hooks/useIsMobile'
import { cn } from '@/lib/utils/helpers'
import { DashboardTileEditModal } from '../components/DashboardTileEditModal'
import {
  deleteDashboardTile,
  getDashboardTiles,
  reorderDashboardTiles,
  saveDashboardTile,
} from '../services/dashboardTilesService'
import type {
  DashboardTile,
  DashboardTileListParams,
  DashboardTileSortKey,
  DashboardTileSubmitPayload,
  SortDirection,
} from '../types'
import { normalizeDashboardTileHref } from '../utils/tileUrl'
import {
  mapTileUrlToReactPath,
  openDashboardTile,
} from '@/features/dashboard/utils/dashboardTileNavigation'

const DEFAULT_PARAMS: DashboardTileListParams = {
  orderBy: 'tile_name',
  sortBy: 'asc',
}

/** Prefer React module path for href; keep Angular path for handoff click handler. */
function tileListHref(tileUrl: string): string {
  return mapTileUrlToReactPath(tileUrl) || normalizeDashboardTileHref(tileUrl)
}

/**
 * Angular list always opens tile URLs in a new tab via `href` + `target="_blank"`.
 * Resolve React-enabled modules in-app; otherwise OTT handoff to Angular.
 */
function handleTileUrlClick(
  event: React.MouseEvent<HTMLAnchorElement>,
  tile: DashboardTile,
) {
  event.preventDefault()
  event.stopPropagation()
  openDashboardTile({ ...tile, new_tab: '1' })
}

/** `<input type="color">` requires a valid hex; API values may be empty or malformed. */
function normalizeHexForInput(value: string | undefined, fallback = '#000000'): string {
  if (!value || typeof value !== 'string') return fallback
  const v = value.trim()
  if (/^#[0-9A-Fa-f]{6}$/i.test(v)) return v
  if (/^#[0-9A-Fa-f]{3}$/i.test(v)) {
    return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`
  }
  return fallback
}

/**
 * Angular list shows tile_name with default text color (not `font_color`).
 * Icons render without inline color — colors appear only in the Icon Color / Background Color columns.
 */
function DashboardTileIconCell({ tileIcon }: { tileIcon?: string }) {
  const cls = tileIcon?.trim()
  if (!cls) return <span className="text-slate-300">—</span>
  return (
    <i
      className={`${cls} inline-flex items-center justify-center min-w-[1.25rem] text-lg leading-none text-slate-700`}
      aria-hidden
    />
  )
}

export function DashboardTilesListPage() {
  const [tiles, setTiles] = useState<DashboardTile[]>([])
  const [params, setParams] = useState<DashboardTileListParams>(DEFAULT_PARAMS)
  const [loading, setLoading] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<DashboardTile | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<DashboardTile | null>(null)
  // Mirrors Angular's `isEdited` flag: set to true when the user
  // reorders rows via drag-drop, reset after Save Order succeeds.
  const [isEdited, setIsEdited] = useState(false)
  const [savingOrder, setSavingOrder] = useState(false)
  const dragIndexRef = useRef<number | null>(null)
  const mountedRef = useRef(false)
  const isMobile = useIsMobile()
  // Matches Angular: table on desktop, card grid on mobile only (no manual toggle).
  const isListView = !isMobile

  const load = useCallback(async (next: DashboardTileListParams) => {
    setLoading(true)
    try {
      setTiles(await getDashboardTiles(next))
      setIsEdited(false)
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true
    void load(DEFAULT_PARAMS)
  }, [load])

  const applyParams = useCallback(
    (next: DashboardTileListParams) => {
      setParams(next)
      void load(next)
    },
    [load],
  )

  const toggleSort = (key: DashboardTileSortKey) => {
    const nextDir: SortDirection =
      params.orderBy === key && params.sortBy === 'asc' ? 'desc' : 'asc'
    applyParams({ ...params, orderBy: key, sortBy: nextDir })
  }

  const openAdd = () => {
    setEditTarget(null)
    setEditOpen(true)
  }

  const openEdit = (tile: DashboardTile) => {
    setEditTarget(tile)
    setEditOpen(true)
  }

  const handleSubmit = async (payload: DashboardTileSubmitPayload, tileId?: number) => {
    setSubmitting(true)
    try {
      await saveDashboardTile(payload, tileId)
      trackApplicationEvent('dashboard_tile_saved', 'system', { action_result: tileId ? 'updated' : 'created' })
      showSuccess('Data Updated Successfully')
      setEditOpen(false)
      setEditTarget(null)
      void load(params)
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitting(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget?.tile_id) return
    try {
      await deleteDashboardTile(deleteTarget.tile_id)
      showSuccess('Tile Deleted Successfully!')
      setDeleteTarget(null)
      void load(params)
    } catch (error) {
      showApiError(error)
    }
  }

  // Drag-drop reorder. Mirrors Angular's cdkDropList + dropRow($event):
  // moves the dragged row to the new index, renumbers tile_order, sets
  // isEdited to true so the Save Order button appears.
  /** Drag only from grip handle — matches Angular `cdkDragHandle`; avoids blocking URL link clicks on the row (QA 83). */
  const handleGripDragStart = (index: number) => (event: React.DragEvent) => {
    dragIndexRef.current = index
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', String(index))
  }

  const handleGripDragEnd = () => {
    dragIndexRef.current = null
  }

  const handleDragOver = (event: React.DragEvent<Element>) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (toIndex: number) => (event: React.DragEvent<Element>) => {
    event.preventDefault()
    const fromIndex = dragIndexRef.current
    dragIndexRef.current = null
    if (fromIndex === null || fromIndex === toIndex) return
    setTiles((current) => {
      const next = [...current]
      const [moved] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, moved)
      return next.map((tile, idx) => ({ ...tile, tile_order: idx + 1 }))
    })
    setIsEdited(true)
  }

  const saveOrder = async () => {
    setSavingOrder(true)
    try {
      await reorderDashboardTiles(tiles)
      showSuccess('Tiles order updated successfully')
      setIsEdited(false)
    } catch (error) {
      showApiError(error)
    } finally {
      setSavingOrder(false)
    }
  }

  const SortIndicator = ({ column }: { column: DashboardTileSortKey }) => {
    if (params.orderBy !== column) return null
    return params.sortBy === 'asc' ? (
      <ArrowUp className="inline h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="inline h-3 w-3 ml-1" />
    )
  }

  return (
    <PageShell
      title="DASHBOARD TILES"
      headerAction={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={openAdd}
            className={cn(primaryEntityActionButtonClass, 'w-full sm:w-auto')}
          >
            Add Tile
          </button>
          {isEdited && (
            <button
              type="button"
              onClick={saveOrder}
              disabled={savingOrder}
              className={cn(primaryEntityActionButtonClass, 'w-full sm:w-auto')}
            >
              {savingOrder ? 'Saving…' : 'Save Order'}
            </button>
          )}
        </div>
      }
    >
      {isListView ? (
      <ListingDataTableCard>
        <ListingDataTableScroll>
          <table className={LISTING_DATA_TABLE_CLASS}>
            <thead className="border-b border-[#c8d7e8] bg-[#dfeaf5] text-slate-900">
              <tr>
                <th
                  className="w-20 cursor-pointer select-none px-4 py-3 text-center text-xs font-bold"
                  onClick={() => toggleSort('tile_order')}
                >
                  Order
                  <SortIndicator column="tile_order" />
                </th>
                <th
                  className="cursor-pointer select-none px-4 py-3 text-left text-xs font-bold"
                  onClick={() => toggleSort('tile_name')}
                >
                  Name
                  <SortIndicator column="tile_name" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold">Description</th>
                <th className="w-12 px-4 py-3 text-center text-xs font-bold">Icon</th>
                <th className="w-24 px-4 py-3 text-center text-xs font-bold">Icon Color</th>
                <th className="w-24 px-4 py-3 text-center text-xs font-bold">Background Color</th>
                <th
                  className="cursor-pointer select-none px-4 py-3 text-left text-xs font-bold"
                  onClick={() => toggleSort('tile_url')}
                >
                  URL
                  <SortIndicator column="tile_url" />
                </th>
                <th className="px-4 py-3 text-center text-xs font-bold">Action</th>
              </tr>
            </thead>
          <tbody className="divide-y divide-[#dfe7f0] [&>tr:nth-child(even)]:bg-[#f2f7fd]">
            {loading ? (
              <tr>
                <td colSpan={8} className="px-4 py-4">
                  <ModuleScreenLoading minHeight="200px" className="py-8" />
                </td>
              </tr>
            ) : tiles.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                  No dashboard tiles.
                </td>
              </tr>
            ) : (
              tiles.map((tile, index) => (
                <tr key={tile.tile_id} onDragOver={handleDragOver} onDrop={handleDrop(index)}>
                  <td className="px-4 py-3 text-center text-slate-700">{tile.tile_order ?? '—'}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{tile.tile_name}</td>
                  <td className="px-4 py-3 text-slate-700">
                    {(tile.tile_description || '').slice(0, 60)}
                    {(tile.tile_description || '').length > 60 ? '…' : ''}
                  </td>
                  <td className="px-4 py-3 text-center align-middle">
                    <div className="flex justify-center">
                      <DashboardTileIconCell tileIcon={tile.tile_icon} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center align-middle">
                    <input
                      type="color"
                      value={normalizeHexForInput(tile.icon_color)}
                      readOnly
                      disabled
                      aria-label="Icon color"
                      className="h-8 w-12 border border-slate-200 rounded cursor-not-allowed bg-white mx-auto block"
                    />
                  </td>
                  <td className="px-4 py-3 text-center align-middle">
                    <input
                      type="color"
                      value={normalizeHexForInput(tile.font_color)}
                      readOnly
                      disabled
                      aria-label="Background color"
                      className="h-8 w-12 border border-slate-200 rounded cursor-not-allowed bg-white mx-auto block"
                    />
                  </td>
                  <td className="px-4 py-3 text-slate-600 font-mono text-xs">
                    {tile.tile_url ? (
                      <a
                        href={tileListHref(tile.tile_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => handleTileUrlClick(e, tile)}
                        className="inline-flex items-center gap-1 text-[#1597ff] hover:text-[#0d7acc]"
                      >
                        {tile.tile_url}
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3 text-center align-middle">
                    <div className="flex gap-1 justify-center">
                      <button
                        type="button"
                        onClick={() => openEdit(tile)}
                        className="p-1.5 rounded text-[#1597ff] hover:bg-[#1597ff]/10"
                        aria-label="Edit"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        draggable
                        onDragStart={handleGripDragStart(index)}
                        onDragEnd={handleGripDragEnd}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-600 cursor-grab active:cursor-grabbing"
                        aria-label="Move"
                        title="Move"
                      >
                        <GripVertical className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(tile)}
                        className="p-1.5 rounded hover:bg-red-50 text-red-600"
                        aria-label="Delete"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </ListingDataTableScroll>
      </ListingDataTableCard>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {loading ? (
            <div className="col-span-full">
              <ModuleScreenLoading minHeight="200px" className="py-8" />
            </div>
          ) : tiles.length === 0 ? (
            <p className="col-span-full text-center text-sm text-slate-500 py-8">
              No dashboard tiles.
            </p>
          ) : (
            tiles.map((tile, index) => (
              <div
                key={tile.tile_id}
                onDragOver={handleDragOver}
                onDrop={handleDrop(index)}
                className="bg-white border border-slate-200 rounded-lg p-4 space-y-3"
              >
                <div className="flex items-center gap-3">
                  <DashboardTileIconCell tileIcon={tile.tile_icon} />
                  <p className="text-sm font-medium text-slate-900">{tile.tile_name}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500">Order</p>
                  <p className="text-sm text-slate-700">{tile.tile_order ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500">Description</p>
                  <p className="text-sm text-slate-700">
                    {(tile.tile_description || '').slice(0, 60)}
                    {(tile.tile_description || '').length > 60 ? '…' : ''}
                  </p>
                </div>
                <div className="flex gap-3">
                  <div>
                    <p className="text-xs font-medium text-slate-500">Icon Color</p>
                    <input
                      type="color"
                      value={normalizeHexForInput(tile.icon_color)}
                      readOnly
                      disabled
                      aria-label="Icon color"
                      className="h-8 w-12 border border-slate-200 rounded cursor-not-allowed bg-white"
                    />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500">Background Color</p>
                    <input
                      type="color"
                      value={normalizeHexForInput(tile.font_color)}
                      readOnly
                      disabled
                      aria-label="Background color"
                      className="h-8 w-12 border border-slate-200 rounded cursor-not-allowed bg-white"
                    />
                  </div>
                </div>
                {tile.tile_url && (
                  <div>
                    <p className="text-xs font-medium text-slate-500">URL</p>
                    <a
                      href={tileListHref(tile.tile_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => handleTileUrlClick(e, tile)}
                      className="inline-flex items-center gap-1 break-all font-mono text-sm text-[#1597ff] hover:text-[#0d7acc]"
                    >
                      {tile.tile_url}
                    </a>
                  </div>
                )}
                <div className="flex gap-1 justify-end pt-2 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => openEdit(tile)}
                    className="p-1.5 rounded text-[#1597ff] hover:bg-[#1597ff]/10"
                    aria-label="Edit"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    draggable
                    onDragStart={handleGripDragStart(index)}
                    onDragEnd={handleGripDragEnd}
                    className="p-1.5 rounded hover:bg-slate-100 text-slate-600 cursor-grab active:cursor-grabbing"
                    aria-label="Move"
                    title="Move"
                  >
                    <GripVertical className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteTarget(tile)}
                    className="p-1.5 rounded hover:bg-red-50 text-red-600"
                    aria-label="Delete"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <DashboardTileEditModal
        isOpen={editOpen}
        tile={editTarget}
        submitting={submitting}
        onClose={() => {
          setEditOpen(false)
          setEditTarget(null)
        }}
        onSubmit={handleSubmit}
      />

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="Delete Dashboard Tile"
        message="Are You Sure To Delete this Tile ?"
        primaryLabel="Delete"
        onPrimary={confirmDelete}
        secondaryLabel="Cancel"
        onSecondary={() => setDeleteTarget(null)}
        variant="warning"
      />
    </PageShell>
  )
}
